<template>
  <div id="progress-graph" class="flex flex-col">
    <div class="flex justify-between">
      <div class="mb-4 text-xs text-grey-400">
        {{ yAxisLabel }}
      </div>
      <div v-if="targetType === 'Target::Sbt'" class="mb-4 text-xs text-grey-400">
        Frequency
      </div>
    </div>
    <div class="flex justify-center">
      <div class="relative flex">
        <div
          v-if="targetType !== 'Target::Sbt'"
          :style="
            targetType === 'Target::Duration'
              ? { width: `${yAxesWidth * 2}px` }
              : { width: `${yAxesWidth}px` }
          "
          class=""
        >
          <LineChart
            :key="'y-axes-line-chart'"
            :options="yAxesLineChartOptions"
            :chart-data="yAxesLineChartCollection"
            class="h-120"
          />
        </div>
        <div
          id="scrollable-chart"
          :style="
            targetType === 'Target::Duration'
              ? { maxWidth: `calc(${defaultWidth}px - ${yAxesWidth * 2}px)` }
              : { maxWidth: `calc(${defaultWidth}px - ${yAxesWidth}px)` }
          "
          class="overflow-x-auto scrollbar-lg"
        >
          <div
            v-if="targetType === 'Target::Sbt'"
            :style="{ width: `calc(${widthChart}px - ${yAxesWidth}px)` }"
            class="h-120"
            :key="`mixed-chart-${targetType}`"
          >
            <MixedChart
              :chart-data="mixedChartData"
              :chart-grid-show="isGridShow"
            />
          </div>
          <LineChart
            v-else
            :key="`line-chart-${targetType}`"
            :style="
              targetType === 'Target::Duration'
                ? { width: `calc(${widthChart}px - ${yAxesWidth * 2}px)` }
                : { width: `calc(${widthChart}px - ${yAxesWidth}px)` }
            "
            :options="lineChartOptions"
            :chart-data="lineChartCollection"
            :plugins="[multiArbitraryLine]"
            class="h-120"
          />
        </div>
      </div>
    </div>
    <div class="self-center mt-2 text-xs text-grey-400">Date</div>
  </div>
</template>

<script>
import LineChart from "../../charts/LineChart.js";
import MixedChart from "../../charts/MixedChart.vue";

export default {
  name: "TargetPropertyProgressGraph",
  components: { LineChart, MixedChart },
  props: ["graphData", "targetType", "isGridShow", "present"],
  data() {
    return {
      yAxesWidth: 36, // px
      defaultWidth: 960, // px
      multiArbitraryLine: {
        id: "multiArbitraryLine",
        beforeDatasetsDraw(chart, args, options) {
          const {
            ctx,
            chartArea: { top, bottom },
            scales
          } = chart;
          const x = scales["x-axis-0"];
          const { indexes = [] } = options;
          ctx.save();

          function drawLines(indexPosition) {
            const xPosition = x.getPixelForValue(null, indexPosition);
            //
            ctx.strokeStyle = "#E4E4E4";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.setLineDash([10, 10]);
            ctx.moveTo(xPosition, top);
            ctx.lineTo(xPosition, bottom);
            ctx.stroke();
            ctx.closePath();
            ctx.restore();
            ctx.setLineDash([]);
          }

          for (let index = 0; index < indexes.length; index++) {
            drawLines(indexes[index]);
          }
        }
      }
    };
  },
  computed: {
    yAxisLabel() {
      if (this.targetType.includes("Percentage")) return "Score (%)";
      if (this.targetType.includes("Duration")) return "Duration";
      if (this.targetType.includes("Pir")) return "Percentage (%)";
      if (this.targetType.includes("Frequency")) return "Frequency";
      if (this.targetType.includes("Prompting")) {
        if (this.present === "frequency") return "Frequency";
        if (this.present === "percentage") return "Percentage (%)";
        if (this.present === "final_score") return "Final Score (%)";
      }
      if (this.targetType.includes("Sbt")) return "Score (%)";
    },
    widthChart() {
      const labels = this.graphData.labels ? this.graphData.labels.length : 0;
      const scale = labels * 52;
      return scale < this.defaultWidth ? this.defaultWidth : scale;
    },
    //
    lineChartOptions() {
      const { labels = [], recorded_by: recordedBy = [] } = this.graphData;
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) phaseLineIndexes.push(idx);
      });
      //
      const options = {
        legend: { display: false },
        maintainAspectRatio: false,
        animation: false,
        tooltips: {
          usePointStyle: true,
          callbacks: {
            afterTitle: function(context) {
              if (recordedBy.at(context[0].index)) {
                return `Recorded by: ${recordedBy.at(context[0].index)}`;
              }
            }
          }
        },
        layout: { padding: { top: 6 } },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                display: false
              },
              afterFit: ctx => {
                ctx.width = 0;
              },
              gridLines: {
                display: this.isGridShow,
                drawBorder: false
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: this.isGridShow,
                drawBorder: true
              }
            }
          ]
        },
        plugins: {
          multiArbitraryLine: {
            indexes: phaseLineIndexes
          }
        }
      };
      if (this.targetType === "Target::Duration") {
        options.tooltips = {
          usePointStyle: true,
          callbacks: {
            title: a => a[0].xLabel.join("/"),
            label: a => this.formatTime(Number(a.value || 0)),
            afterTitle: function(context) {
              if (recordedBy.at(context[0].index)) {
                return `Recorded by: ${recordedBy.at(context[0].index)}`;
              }
            }
          }
        };
      }
      if (
        this.targetType === "Target::Percentage" ||
        this.targetType === "Target::Pir" ||
        this.targetType === "Target::Sbt" ||
        (this.targetType === "Target::Prompting" &&
          (this.present === "percentage" || this.present === "final_score"))
      ) {
        options.scales.yAxes[0].ticks = {
          beginAtZero: true,
          display: false,
          max: 100
        };
      }
      return options;
    },
    yAxesLineChartOptions() {
      const options = {
        legend: { display: false },
        maintainAspectRatio: false,
        animation: false,
        tooltips: { enabled: false },
        layout: { padding: { bottom: 34 } },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: false,
                drawBorder: !this.isGridShow
              },
              afterFit: ctx => {
                ctx.width = this.yAxesWidth;
              }
            }
          ],
          xAxes: [
            {
              ticks: { display: false },
              gridLines: {
                display: false,
                drawBorder: false
              }
            }
          ]
        }
      };
      if (this.targetType === "Target::Duration") {
        options.scales.yAxes[0].ticks = {
          beginAtZero: true,
          fontColor: "#707070",
          fontSize: 12,
          callback: this.formatTime
        };
        options.scales.yAxes[0].afterFit = ctx => {
          ctx.width = this.yAxesWidth * 2;
        };
      }
      if (
        this.targetType === "Target::Percentage" ||
        this.targetType === "Target::Pir" ||
        this.targetType === "Target::Sbt" ||
        (this.targetType === "Target::Prompting" &&
          (this.present === "percentage" || this.present === "final_score"))
      ) {
        options.scales.yAxes[0].ticks = {
          beginAtZero: true,
          fontColor: "#707070",
          fontSize: 12,
          max: 100
        };
      }
      return options;
    },
    //
    lineChartCollection() {
      const { labels = [], datasets = [], mixed = false } = this.graphData;
      const datacollection = { labels, datasets, mixed };
      return datacollection;
    },
    yAxesLineChartCollection() {
      const { labels = [], datasets = [] } = this.graphData;
      const datacollection = {
        labels,
        datasets: datasets.map(i => {
          return {
            data: i.data,
            backgroundColor: "",
            borderColor: "",
            pointRadius: 0,
            fill: false,
            tension: 0.1
          };
        })
      };
      return datacollection;
    },
    mixedChartData() {
      const { labels = [], datasets = [] } = this.graphData;

      const taskDatasets = datasets.filter(dataset => "taskId" in dataset);
      const behaviorDatasets = datasets.filter(
        dataset => "behaviorId" in dataset
      );

      return {
        labels,
        datasets: [...taskDatasets, ...behaviorDatasets],
        recorded_by: this.graphData.recorded_by
      };
    }
  },
  mounted() {
    this.setupAvailableSpace();

    setTimeout(() => {
      const scrollableChart = document.getElementById("scrollable-chart");
      if (scrollableChart) {
        scrollableChart.scrollTo({
          right: 0,
          left: scrollableChart.scrollWidth,
          behavior: "smooth"
        });
      }
    }, 300);
  },
  methods: {
    setupAvailableSpace() {
      const progressGraph = document.getElementById("progress-graph");
      if (progressGraph) {
        this.defaultWidth = progressGraph.offsetWidth;
      }
    },
    formatTime(x) {
      let hours = "00";
      let minutes = "00";
      let seconds = "00";
      if (typeof x === "number") {
        seconds = (x % 60).toString().padStart(2, "0");
        if (x >= 60) {
          minutes = Math.floor(x / 60);
          if (x >= 3600) minutes = minutes % 60;
          minutes = minutes.toString().padStart(2, "0");
        }
        if (x >= 3600) {
          hours = Math.floor(x / 3600)
            .toString()
            .padStart(2, "0");
        }
      }
      return `${hours}:${minutes}:${seconds}`;
    }
  },
  beforeDestroy() {
    if (this.mixedChart) {
      this.mixedChart.destroy();
    }
    if (this.lineChart) {
      this.lineChart.destroy();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/ProgressGraph.vue